import type { PostgrestError, RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import type { ExpenseModel } from "../../reduxToolkit/expensesSlice"
import type { IncidentModel } from "../../reduxToolkit/incidentsSlice"
import type { AttachmentModel } from "../../reduxToolkit/attachmentsSlice"
import type { SubmissionModel } from "../../reduxToolkit/submissionsSlice"
import type { NotificationModel } from "../../reduxToolkit/notificationsSlice"
import { markBlobBytesUploaded, type BlobModel } from "../../reduxToolkit/blobsSlice"
import type { ConversationModel, MessageModel, PhoneCallModel } from "../../reduxToolkit/conversationsSlice"
import type { RootState, AppDispatch } from "../../reduxToolkit/store"
import { AppSupabaseClient, Data, isDeleted } from "../../types/supabase"
import { isUnsyncable, onSyncUpComplete, resetSyncCursor, setSyncCursor } from "../../reduxToolkit/syncSlice"
import { findPendingAction, isUnrecoverable, UnrecoverableSyncError } from "./unrecoverable"
import { Add, SyncableTable, SyncableTables, SyncAction, SyncResult, raiseUnknownSyncableTable } from "./action"
import { NeedsUpload, ReconciledTables, SyncResponse, postSync } from "./syncBatch"
import { SyncDigestPayload, digestOf } from "./syncDigest"
import { OnSyncDownCompletePayload, onSyncDownComplete } from "../../reduxToolkit/actions/onSyncDownComplete"
import { convertServerTimestampsToISO } from "../supabase/util"
import { AdvanceRequestModel, AdvanceModel } from "../../reduxToolkit/advancesSlice"
import { EventEmitter } from '../util/eventEmitter'
import type { AccessTokenProvider } from '../../backend/httpTransport'
import { ProviderModel } from '../../reduxToolkit/providersSlice'
import { RealizedTodoModel } from '../../reduxToolkit/todosSlice'
import type { BackendClient } from '../../platform/backendClient'
import type { FileStoreAdapter } from '../../platform/fileStore'

export interface SyncActorOptions {
  /**
   * Keep a blob's local bytes after they upload instead of evicting them.
   *
   * The web app evicts, which bounds IndexedDB. The native app keeps them: its
   * file store is the member's offline copy of every document, and a re-upload
   * is prevented by `markBlobBytesUploaded` (and the endpoint is idempotent).
   */
  keepUploadedFiles?: boolean
}

/**
 * This class is an Actor that runs in the background to sync the pending
 * actions with the server.  It manages exponential backoff and retries.
 */
export class SyncActor extends EventEmitter {
  private readonly _interval = 1000
  /**
   * How often to run a full `have` reconcile on top of the `since` fast path.
   *
   * This is the third layer, and it is worth being precise about how little is left for it
   * to do. Realtime hydrates a named row by id without consulting the cursor at all, so it
   * delivers writes the watermark has already passed. The cursor covers everything since
   * the watermark, on connect, on foreground, and after each write. What reaches the full
   * diff is only what *both* missed:
   *
   * - A row whose `updated_at` predates our cursor but which became visible after it was
   *   issued -- a client with a slow clock (the endpoint stores the browser's timestamp
   *   verbatim), or a server write inside a transaction that commits later -- AND whose
   *   realtime event never arrived.
   * - A local row the server has never seen, where the queue entry is gone but the record
   *   is not.
   *
   * That intersection is rare and, when it happens, stale rather than corrupting. Against
   * it, `have` uploads the client's whole manifest, which for an active membership is the
   * largest request the app makes.
   *
   * An hour, therefore, rather than something tighter. Note also *when* this fires: a full
   * pass already runs on start and on foreground when overdue, so the interval only reaches
   * a tab that has been continuously in front of the user -- which is the case least likely
   * to have missed anything, since its channel is connected and its cursor is advancing.
   */
  /**
   * How many full reconciles in a row a table may diverge on before we call it pathological
   * and tell Sentry.
   *
   * Three, because one is ordinary -- a write the fast path missed, repaired and gone -- and
   * two can be a second write landing while the first pass was in flight. A table that still
   * disagrees on the third consecutive pass is not converging, and under this protocol that
   * is not a cosmetic problem: it re-downloads the whole table every hour for as long as it
   * lasts. See ADR 009.
   */
  private static readonly DIVERGENCE_RUN_ALERT = 3

  private readonly _fullSyncInterval = 60 * 60 * 1000
  /** Consecutive full reconciles each table has come back diverged on. */
  private readonly _divergedRuns = new Map<SyncableTable, number>()
  /** Tables already reported, so a lasting divergence is one Sentry event and not one an hour. */
  private readonly _reportedDivergence = new Set<SyncableTable>()
  private _lastSucceededAt?: number
  private _lastFullSyncAt?: number
  /**
   * The channel's own last reported status, so a sync-down fires only on the edge into
   * SUBSCRIBED rather than on every callback that happens to report it.
   *
   * The channel is meant to report SUBSCRIBED rarely -- once after a real disconnect -- but
   * a websocket that cannot hold a stable connection (a proxy with an aggressive idle
   * timeout, a captive portal, a bad wifi signal) can have realtime-js re-affirm SUBSCRIBED
   * repeatedly without an intervening drop, or flap through a reconnect every few seconds.
   * Tracking the previous status turns "fire a sync-down whenever told we're subscribed"
   * into "fire one only when we just weren't" -- a repeated SUBSCRIBED with nothing in
   * between is not a reconnect and has nothing new to report, while an actual
   * SUBSCRIBED-then-dropped-then-SUBSCRIBED cycle still gets its catch-up every time, because
   * each of those really did just come back from being deaf to the channel.
   */
  private _lastChannelStatus: string | undefined
  private _nextSync?: ReturnType<typeof setTimeout>
  private _nextFullSync?: ReturnType<typeof setTimeout>
  private _channel: RealtimeChannel | undefined

  public get lastSucceededAt() {
    return this._lastSucceededAt
  }

  constructor(
    private readonly _supabase: AppSupabaseClient,
    private readonly _store: { getState: () => RootState },
    private readonly _dispatch: AppDispatch,
    private readonly _tables: Readonly<SyncableTable[]> = SyncableTables,
    /**
     * How this actor reaches the backend -- every write and every sync-down goes
     * through it. No default: this class is shared code with no web coupling, so the
     * host supplies its own client (the web app injects `webBackendClient`, whose base
     * URL is empty so requests stay origin-relative; React Native has no document
     * origin and must inject a client with a real base URL) -- see
     * docs/plans/embeddable-sdk.md.
     */
    private readonly _backend: BackendClient,
    /**
     * Where captured-but-not-yet-uploaded file bytes live. No default, for the same
     * reason as `_backend` -- the web app injects `webFileStore`; React Native must
     * inject its own -- see docs/plans/embeddable-sdk.md.
     */
    private readonly _fileStore: FileStoreAdapter,
    /**
     * Where the bearer token for backend calls comes from.
     *
     * Omitted, it is the Supabase client's own session, which is what the web
     * app relies on. `@healthshare/sdk` passes the host's `AuthAdapter`
     * instead, because its Supabase client deliberately holds no session at
     * all: pushing one in with `auth.setSession()` would make the SDK a second
     * owner of the refresh token, and GoTrue's `setSession` refreshes it
     * outright when the access token has expired -- regardless of
     * `autoRefreshToken: false`. Two refreshers each invalidate the other's
     * token, which is the invariant docs/plans/expo-owned-signin.md exists to
     * protect.
     */
    private readonly _getAccessToken?: AccessTokenProvider,
    private readonly _options: SyncActorOptions = {}
  ) {
    super()
  }

  public start() {
    const membershipId = this._store.getState()?.membership?.membershipId
    if (!membershipId) {
      this.emit('info', 'no membershipId, not starting')
      return
    }

    let channel = this._supabase.channel('sync')
    channel = this._tables.reduce((ch, table) => {
      return channel.on('postgres_changes',
      {
        schema: 'public',
        table: table,
        event: '*',
        filter: `membership_id=eq.${membershipId}`
      } , (payload) => {
        this.onRealtimeUpdate(table, payload as RealtimePostgresChangesPayload<Data<typeof table>>)
          .catch((error) => this.emit('error', error))
      })
    }, channel)

    // Start syncing straight away rather than waiting for the channel.
    //
    // The loop used to start on SUBSCRIBED and stop on anything else, which meant a client
    // that could not open a websocket -- a captive portal, a proxy that blocks upgrades, a
    // flaky connection -- synced nothing at all, silently. Now that sync-up and sync-down
    // are ordinary HTTP, the channel is only a doorbell, and losing it should cost latency
    // rather than correctness.
    clearTimeout(this._nextSync)
    this._nextSync = setTimeout(() => this._syncUpWorker(), this._interval)
    this.syncDown('full')
      .catch((error) => this.emit('error', error))

    this._scheduleFullSync()

    // Reset so this start()'s first SUBSCRIBED is always treated as an edge, even if a
    // previous run of this same actor left it at 'SUBSCRIBED' before stop() tore the old
    // channel down.
    this._lastChannelStatus = undefined

    this._channel = channel.subscribe((status, err) => {
      this.emit('info', `channel status: ${status}`)
      const previousStatus = this._lastChannelStatus
      this._lastChannelStatus = status

      if (status === 'SUBSCRIBED') {
        if (previousStatus === 'SUBSCRIBED') {
          // Already were -- this is a re-affirmation, not a reconnect, and there is nothing
          // it could have caught that a sync-down would find.
          return
        }

        // Reconnected: anything that changed while we were deaf to the channel is caught
        // by a sync-down now, without waiting for the next scheduled one.
        this.syncDown()
          .catch((error) => this.emit('error', error))
      } else if (err) {
        this.emit('error', err)
      }
    })
  }

  /**
   * Keep the full reconcile on a timer for as long as the actor runs. Self-rescheduling
   * rather than an interval so a slow pass cannot overlap the next one, and errors are
   * emitted rather than thrown: a failed reconcile should cost one cycle, not the loop.
   */
  private _scheduleFullSync() {
    clearTimeout(this._nextFullSync)
    this._nextFullSync = setTimeout(() => {
      this.syncDown('full')
        .catch((error) => this.emit('error', error))
        .finally(() => this._scheduleFullSync())
    }, this._fullSyncInterval)
  }

  /**
   * Sync down after a stretch where our own timers may not have run.
   *
   * Called when the app comes back to the foreground. Two things make that worth doing
   * rather than leaving it to the interval above:
   *
   * - **The interval measures foreground time, not wall clock.** Browsers throttle timers
   *   in hidden tabs and stop them outright in the back/forward cache, and React Native
   *   throttles them while backgrounded — the same reason nativeAuth.ts installs a
   *   visibility backstop for token refresh. A tab that lives mostly in the background
   *   could otherwise go a very long time between reconciles.
   * - **Sync-down is still the part that leans on the realtime channel.** Sync-up runs on
   *   its own loop now, but the only routine sync-*down* triggers are this interval and the
   *   channel resubscribing. A client that cannot hold a websocket would otherwise see new
   *   data at most once an hour.
   *
   * Deliberately not a full reconcile every time: tab-switching is constant and `have`
   * uploads the client's entire manifest, so the full pass runs only when it is actually
   * overdue and the cheap one covers the rest.
   */
  public async catchUp(): Promise<void> {
    const overdue = this._lastFullSyncAt === undefined ||
      Date.now() - this._lastFullSyncAt >= this._fullSyncInterval

    await this.syncDown(overdue ? 'full' : 'fast')
  }

  public stop() {
    if (this._nextSync) {
      clearTimeout(this._nextSync)
    }

    if (this._nextFullSync) {
      clearTimeout(this._nextFullSync)
      this._nextFullSync = undefined
    }

    this._channel?.unsubscribe()
      .catch((error) => {
        this.emit('error', error)
      })
  }

  private async _syncUpWorker(retries: number = 0) {
    try {
      await this.syncUp()

      // Awaited here, inside the success branch, rather than fired off in parallel: this
      // keeps the 1s loop serial, so a slow upload can't overlap the next tick and attempt
      // the same file twice. Its own errors are swallowed rather than rethrown -- a stalled
      // or failing file upload must not trigger the row-sync loop's exponential backoff,
      // since ordinary expense/incident sync should keep working regardless of upload state.
      await this.uploadPendingFiles().catch((error) => this.emit('error', error))

      this._nextSync = setTimeout(() => this._syncUpWorker(), this._interval)
    } catch (error: any) {

      this.emit('error', error)

      // backoff
      retries++
      const delay = Math.min(2 ** retries * 1000, 60000)
      this._nextSync = setTimeout(() => this._syncUpWorker(retries), delay)
    }
  }

  /**
   * Uploads one captured-but-not-yet-uploaded blob's bytes, oldest first.
   *
   * "Pending" is derived from state this device already holds, not a separate queue: a row
   * with `bytes_uploaded_at` still null that this device has local bytes for. A row whose
   * bytes only exist on another device (bytesUploadedAt null, but fileStore.get() misses)
   * is not this device's to upload -- it's waiting on that other device, or already in
   * flight to the server.
   *
   * On success the local row is marked uploaded. The content endpoint's own
   * `bytes_uploaded_at` still arrives through realtime or sync-down — that is
   * the server's timestamp — but waiting for it left the row on "Uploading…"
   * whenever that doorbell sorted behind the client's millisecond `updated_at`
   * and was dropped. The mark is not a sync write: the column is server-owned.
   * The local file bytes are evicted from the file store on success instead --
   * see the `fileStore.delete()` call below -- unless the host keeps them
   * (`keepUploadedFiles`), as the native app does so documents open offline.
   */
  public async uploadPendingFiles(): Promise<void> {
    const state = this._store.getState()
    const membershipId = state.membership?.membershipId
    if (!membershipId) { return }

    const pending = (state.blobs?.blobs ?? [])
      .filter((b) => !b.bytes_uploaded_at && !b.deleted_at)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))

    if (pending.length === 0) { return }

    const accessToken = await this._accessToken()
    if (!accessToken) { return }

    for (const blob of pending) {
      const stored = await this._fileStore.get(blob.key)
      if (!stored) { continue }

      try {
        const resp = await this._backend.fetch(
          `/backend/memberships/${membershipId}/blobs/${blob.key}/content`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': stored.contentType,
            },
            body: stored.bytes,
          }
        )
        if (resp.ok) {
          // Evict the local copy: it's durably in Storage now, so keeping it would grow
          // IndexedDB unbounded, and clearing it also prevents a redundant re-upload next
          // tick if the realtime update that lands `bytes_uploaded_at` is delayed or missed
          // (fileStore.get() above then misses and this row is skipped, not re-sent).
          if (!this._options.keepUploadedFiles) {
            await this._fileStore.delete(blob.key)
          }
          // The PUT returning ok means the bytes are in storage (a repeat PUT is
          // idempotent once they are). Record that locally so the row stops
          // reading as pending without waiting for the doorbell.
          if (blob.id) {
            this._dispatch(markBlobBytesUploaded({
              id: blob.id,
              bytes_uploaded_at: new Date().toISOString(),
            }))
          }
          return
        }
        this.emit('info', `blob content upload returned ${resp.status} for ${blob.key}`)
      } catch (error) {
        this.emit('info', `blob content upload failed for ${blob.key}: ${error}`)
      }

      // Only one attempt per tick, and only the oldest: a failure here shouldn't block an
      // attempt at a different (possibly smaller, possibly more likely to succeed) file next
      // tick, but trying several at once defeats the point of a slow, serial drain.
      return
    }
  }

  /**
   * Push the queued actions in the redux syncSlice to the server in one request. Errors
   * are thrown so the caller's exponential backoff picks them up.
   */
  public async syncUp() {
    let result: SyncResult | undefined = undefined

    const state = this._store.getState()

    const pending = state.sync?.pending || []
    if (pending.length > 0) {
      // Whatever the server sends back alongside the verdicts — reconciled todos, and on a
      // combined pass the sync-down changes too. Applied after onSyncUpComplete below, so
      // the outbox settles before anything can mark its entries obsolete.
      const response = await this._postSync({ actions: pending })
      // No access token yet: there is nothing to report and nothing to retry, so leave
      // the queue alone and let the next tick pick it up.
      if (!response) { return undefined }
      result = response.result
      if (!result) { return undefined }

      this.emit('info', `sync result: applied ${result.applied.length}, rejected ${result.rejected.length}, error ${result.error.length}`)

      // onSyncUpComplete drops actions the server has refused too many times. Work
      // out which those are before dispatching, while the attempt counts are still
      // readable, so each discard gets reported instead of vanishing quietly.
      const discarded = result.error.filter((errorAction) => {
        const queued = findPendingAction(pending, errorAction)
        return !!queued && isUnrecoverable(queued, errorAction.error)
      })

      this._dispatch(onSyncUpComplete(result))

      discarded.forEach((action) => {
        this.emit('error', new UnrecoverableSyncError(
          action.table,
          action.type,
          action.record.id,
          (action.error as PostgrestError)?.code,
          action.error
        ))
      })

      // Apply what came back before the error check below: the todos the server reconciled
      // are valid regardless of whether some other action in the batch failed, and throwing
      // past them would leave the member's list stale until the next pass.
      if (response) {
        this._applySyncResponse(response)
      }

      if (result.error.length > 0) {
        // throw an error to get into our exponential backoff
        const errorMsg = result.error.map((e) => `${e.table} ${e.type} ${e.record?.id}: ${e.error?.message}`).join(`\n  `)
        throw new SyncError(`Some errors occurred during sync:\n  ${errorMsg}`, result)
      }

    }

    this._lastSucceededAt = Date.now()
    return result
  }


  /**
   * The current Supabase access token, or undefined when we can't get one.
   * Used to authenticate the fire-and-forget calls to the Laravel backend.
   */
  private async _accessToken(): Promise<string | undefined> {
    try {
      if (this._getAccessToken) {
        return (await this._getAccessToken()) ?? undefined
      }
      const { data: { session } } = await this._supabase.auth.getSession()
      return session?.access_token
    } catch {
      return undefined
    }
  }

  /**
   * This method does a full two-way sync with the server.  It queries the server for the ID and updated at of all
   * records in the database, then compares that with the local state.
   * If any records are updated on the server and not locally, it dispatches the onLoadStateFromServer action to update
   * the local redux store.
   */
  /**
   * Post a batch to the sync endpoint, or return undefined when we have no access token.
   *
   * A missing token is not a failure to retry — it means the session is still being
   * established — so it is reported as "nothing happened" rather than thrown into backoff.
   */
  private async _postSync(request: Parameters<typeof postSync>[3]): Promise<SyncResponse | undefined> {
    const membershipId = this._store.getState()?.membership?.membershipId
    if (!membershipId) { return undefined }

    const accessToken = await this._accessToken()
    if (!accessToken) { return undefined }

    return postSync(this._backend, membershipId, accessToken, request)
  }

  /**
   * Apply a sync response: the rows the server sent, whatever the diff says we owe it back,
   * and the new watermark.
   *
   * One round trip, so the cursor is safe to store in the same breath. That is the whole
   * point of answering a diverging digest with the tables themselves rather than a manifest
   * to be asked against afterwards — there is no half-applied state for the watermark to
   * lie about.
   */
  private _applySyncResponse(response: SyncResponse): void {
    // Read before dispatching: the diff compares what we hold now against what the server
    // just sent, and applying the changes first would make every row agree by construction.
    const needsUpload = this._uploadsAfterReconcile(response)

    const payload: OnSyncDownCompletePayload = { ...response.changes }

    const syncActions = this._actionsForNeededUploads(needsUpload)
    if (syncActions.length > 0) {
      payload.syncActions = syncActions
    }

    this._dispatch(onSyncDownComplete(payload))

    if (response.cursor) {
      this._dispatch(setSyncCursor(response.cursor))
    }
  }

  /**
   * Watch for a table that never converges, and say so once.
   *
   * This is the cost of answering a diverging digest with whole tables rather than a
   * manifest the client then asks against by id: a divergence that repairs itself is one
   * cheap download, and one that never repairs is that table re-downloaded every hour for
   * the life of the membership. ADR 009 takes that trade deliberately — the protocol is
   * simple enough for a third party to implement without our SDK — but it is only a sound
   * trade while the pathological case is visible rather than silent.
   *
   * It has happened twice already in this engine's short life: tombstones counted on one
   * side of the digest and not the other, and server timestamps at a different precision
   * than local ones. Both made every table disagree on every pass, and neither threw.
   *
   * Reported per table and only once per run, so a membership stuck for a week is one event
   * rather than a hundred and sixty-eight. The run resets when the table finally agrees,
   * which also re-arms the report — a divergence that comes back is worth hearing about
   * again.
   */
  private _trackDivergence(reconciled: ReconciledTables): void {
    for (const table of this._tables) {
      if (!reconciled[table]) {
        this._divergedRuns.delete(table)
        this._reportedDivergence.delete(table)
        continue
      }

      const run = (this._divergedRuns.get(table) ?? 0) + 1
      this._divergedRuns.set(table, run)

      if (run < SyncActor.DIVERGENCE_RUN_ALERT || this._reportedDivergence.has(table)) {
        continue
      }

      this._reportedDivergence.add(table)
      this.emit('error', new SyncDivergenceError(table, run, this._rowsHeld(table)))
    }
  }

  private _rowsHeld(table: SyncableTable): number {
    return selectRecords(this._store.getState(), table).length
  }

  /**
   * Work out what the server is missing, from the tables it just sent us in full.
   *
   * `needs_upload` used to come down from the server, which could compute it because the
   * client had uploaded its whole view. It no longer does — so the client answers the
   * question itself, which it can only do for a table the response is *complete* for. That
   * is what `reconciled` marks; on the `since` path it is empty and nothing is inferred,
   * because a row absent from a partial response says nothing at all.
   *
   * The string comparisons are only sound because every `updated_at` in the store is the
   * same fixed-width millisecond ISO — see normalizeChanges in syncBatch.ts. With mixed
   * precision, lexicographic order stops matching chronological order.
   */
  private _uploadsAfterReconcile(response: SyncResponse): NeedsUpload[] {
    const reconciled: ReconciledTables = response.reconciled ?? {}
    const state = this._store.getState()
    const needsUpload: NeedsUpload[] = []

    // OnSyncDownCompletePayload is an intersection of per-table shapes, so it cannot be
    // indexed by a loop variable. The rows carry id and updated_at whatever the table.
    const sections = response.changes as unknown as Record<string, {
      updated?: Array<{ id: string, updated_at: string }>
      deleted?: Array<{ id: string, updated_at: string }>
    } | undefined>

    for (const table of this._tables) {
      // A table the endpoint refuses writes to is not a divergence we can repair upward.
      // Queueing one would wedge the outbox on a write that is rejected forever.
      if (!reconciled[table]?.upload) { continue }

      const server = new Map<string, string>()
      // Tombstones included, deliberately. They are what stops a row the server deleted
      // from reading as one it has never seen, which would re-insert what we just removed.
      for (const row of sections[table]?.updated ?? []) { server.set(row.id, row.updated_at) }
      for (const row of sections[table]?.deleted ?? []) { server.set(row.id, row.updated_at) }

      for (const [id, mine] of this._localRows(state, table)) {
        const theirs = server.get(id)

        if (theirs === undefined) {
          needsUpload.push({ table, id, on_server: false })
        } else if (mine > theirs) {
          needsUpload.push({ table, id, on_server: true })
        }
      }
    }

    return needsUpload
  }

  /**
   * Turn the server's "I don't have this, or mine is older" list back into sync actions.
   *
   * The server cannot do this itself — it has the ids but not the record bodies — so this
   * is the same divergence repair syncDown has always done, with the server answering the
   * one question the client cannot: whether the row exists there at all.
   */
  private _actionsForNeededUploads(needsUpload: NeedsUpload[]): SyncAction<SyncableTable>[] {
    if (needsUpload.length === 0) { return [] }

    const state = this._store.getState()
    const actions: SyncAction<SyncableTable>[] = []

    for (const { table, id, on_server } of needsUpload) {
      const record =
        selectRecords(state, table).find((r) => r.id === id) ??
        state.sync.deletedLocally?.[table]?.find((r) => r.id === id)

      // Gone locally since the manifest was built. Nothing to upload.
      if (!record) { continue }

      // Already queued at this version or later — re-queueing would duplicate the write.
      const alreadyPending = state.sync?.pending?.find((p) =>
        p.table === table &&
        p.record.id === id &&
        p.record.updated_at >= record.updated_at)
      if (alreadyPending) { continue }

      // We gave up on this exact version once. Re-queueing it would put the client straight
      // back into the retry loop the discard broke it out of.
      if (isUnsyncable(state.sync, table, record)) { continue }

      if (isDeleted(record)) {
        actions.push({ type: 'delete', table, record })
      } else if (on_server) {
        actions.push({ type: 'update', table, record })
      } else {
        actions.push({ type: 'add', table, record } as Add<typeof table>)
      }
    }

    return actions
  }

  /**
   * Everything this client holds for one table, as id -> updated_at.
   *
   * Includes records deleted locally, since the server has to hear about those too, and
   * dedupes by id with the later entry winning — deletedLocally has no dedupe on push, so a
   * row deleted twice appears twice.
   *
   * Only the upload diff reads this. _buildDigest deliberately does not: the digest has to
   * count the same set the server counts, and neither side counts tombstones.
   */
  private _localRows(state: RootState, table: SyncableTable): Map<string, string> {
    const rows = new Map<string, string>()

    for (const record of selectRecords(state, table)) {
      rows.set(record.id, record.updated_at)
    }
    for (const record of state.sync.deletedLocally?.[table] ?? []) {
      rows.set(record.id, record.updated_at)
    }

    return rows
  }

  /**
   * A per-table summary of what this client holds, small enough to send whatever the size
   * of the membership. Replaces the whole-manifest upload; see syncDigest.ts.
   *
   * Live records only -- deliberately not _localRows(), which also carries deletedLocally.
   * The server hashes its live rows for the same reason: we keep no tombstones. A row we
   * learn the server deleted is spliced out of its slice, and our own deletedLocally entry
   * is pruned the moment the server confirms the delete. If either side counted tombstones,
   * every table that had ever seen a delete would disagree on every pass forever -- and
   * under this protocol that means re-downloading the table every pass, not just re-reading
   * a manifest.
   *
   * Leaving deletedLocally out costs nothing: a delete we have not uploaded yet still makes
   * the table diverge, because the server is still counting that row as live. The diff then
   * queues the upload from _localRows, which does include it.
   */
  private _buildDigest(): SyncDigestPayload {
    const state = this._store.getState()
    const digest: SyncDigestPayload = {}

    for (const table of this._tables) {
      digest[table] = digestOf(
        selectRecords(state, table).map((r): [string, string] => [r.id, r.updated_at])
      )
    }

    return digest
  }

  /**
   * Pull down whatever the client is missing.
   *
   * 'fast' asks only for what changed since the stored watermark; 'full' sends a
   * constant-size digest and repairs divergence in both directions. Fast is the common case, full is
   * its safety net — and the net has to keep being cast, because the fast path can only
   * ever return rows that landed after a point in time, so anything it misses it misses
   * permanently. Without a cursor there is no fast path to take, which is also what makes
   * the first sync after this ships correct for clients rehydrating older state.
   */
  public async syncDown(mode: 'full' | 'fast' = 'fast') {
    const result = await this._syncDownViaBackend(mode)

    if (mode === 'full') {
      // Only on success -- a throw skips this, so a failed pass stays overdue.
      this._lastFullSyncAt = Date.now()
      // Re-arm rather than let the interval fire again moments later. Guarded on the
      // timer still existing so a reconcile racing stop() cannot restart the actor.
      if (this._nextFullSync) { this._scheduleFullSync() }
    }

    return result
  }

  private async _syncDownViaBackend(mode: 'full' | 'fast'): Promise<void> {
    const cursor = this._store.getState().sync?.cursor
    const useFastPath = mode === 'fast' && !!cursor

    try {
      const response = await this._postSync(
        useFastPath ? { since: cursor } : { digest: this._buildDigest() }
      )
      if (!response) { return }

      this._applySyncResponse(response)

      // Only meaningful on the full path: `reconciled` is empty on the fast one because no
      // claim about divergence was made, not because nothing diverged.
      if (!useFastPath) {
        this._trackDivergence(response.reconciled)
      }
    } catch (error) {
      // A failed fast pass leaves a hole: the rows it would have carried sit below the
      // watermark and will never be asked for again. Drop the cursor so the next pass is a
      // full reconcile, which is the only thing that can find them.
      if (useFastPath) {
        this._dispatch(resetSyncCursor())
      }
      throw error
    }
  }

  public async onRealtimeUpdate<T extends SyncableTable>(table: T, payload: RealtimePostgresChangesPayload<Data<T>>) {
    if (payload.eventType === 'DELETE') {
      throw new Error(`Unexpected delete event for table ${table}: ${payload}`)
    }

    this.emit('info', `onRealtimeUpdate: ${table} ${payload.eventType} ${payload.new.id} ${payload.new.updated_at}`)

    const state = this._store.getState()

    const record = payload.new
    // find the record locally
    const localRecord = selectRecords(state, table).find((r) => r.id === record.id)
    if (localRecord) {
      // if the local record is equivalent or newer, ignore the update — unless
      // this is the upload timestamp landing on a blob. Eloquent writes that
      // at whole seconds, which sorts behind the client's millisecond stamp
      // from the same second, and dropping it leaves "Uploading…" up after
      // the bytes are in storage.
      if (localRecord.updated_at >= record.updated_at && !blobUploadArrived(table, localRecord, record)) {
        return
      }
    } else {
      // No local record - check if deleted locally
      const locallyDeleted = state.sync.deletedLocally?.[table]?.find((r) => r.id === record.id)
      // if the local record is equivalent or newer, ignore the update
      if (locallyDeleted && locallyDeleted.updated_at >= record.updated_at) {
        return
      }
    }

    // fetch the full record from the server
    const result = await this._supabase.from(table)
      .select('*')
      .eq('id', record.id)

    if (isDeleted(record)) {
      this._dispatch(onSyncDownComplete({
        [table]: {
          deleted: result.data?.map(convertServerTimestampsToISO)
        }
      }))
    } else {
      this._dispatch(onSyncDownComplete({
        [table]: {
          updated: result.data?.map(convertServerTimestampsToISO)
        }
      }))
    }
  }
}

/**
 * A table whose digest has disagreed with the server's on several consecutive full
 * reconciles.
 *
 * Not a failed request — every pass succeeded and the rows arrived. It means the two sides
 * cannot agree about what they hold, so the reconcile is re-downloading that table in full
 * on every pass instead of finding nothing to do. Carries no row values: this reaches
 * Sentry.
 */
export class SyncDivergenceError extends Error {
  constructor(
    public readonly table: SyncableTable,
    public readonly passes: number,
    public readonly rowsHeld: number,
  ) {
    super(`sync table ${table} has diverged on ${passes} consecutive full reconciles ` +
      `(${rowsHeld} rows held locally); it is being re-downloaded in full every pass`)
    this.name = 'SyncDivergenceError'
  }
}

export class SyncError extends Error {
  public readonly result: SyncResult | undefined

  constructor(message: string, result: SyncResult | undefined) {
    super(message)
    this.result = result
  }
}

export type SyncableModel = ExpenseModel | IncidentModel | AttachmentModel | SubmissionModel | AdvanceModel | AdvanceRequestModel | NotificationModel | ProviderModel | RealizedTodoModel | BlobModel | ConversationModel | MessageModel | PhoneCallModel

/**
 * A blob row whose bytes just landed, even though its `updated_at` does not
 * sort ahead of the copy we already hold.
 */
function blobUploadArrived(
  table: SyncableTable,
  local: SyncableModel,
  incoming: object,
): boolean {
  if (table !== 'blobs') { return false }
  const localUploadedAt = (local as BlobModel).bytes_uploaded_at
  const incomingUploadedAt = (incoming as { bytes_uploaded_at?: string | null }).bytes_uploaded_at
  return !localUploadedAt && !!incomingUploadedAt
}

function selectRecords(state: RootState, table: 'expenses'): Array<ExpenseModel>
function selectRecords(state: RootState, table: 'incidents'): Array<IncidentModel>
function selectRecords(state: RootState, table: 'attachments'): Array<AttachmentModel>
function selectRecords(state: RootState, table: 'submissions'): Array<SubmissionModel>
function selectRecords(state: RootState, table: 'advances'): Array<AdvanceModel>
function selectRecords(state: RootState, table: 'advance_requests'): Array<AdvanceRequestModel>
function selectRecords(state: RootState, table: 'notifications'): Array<NotificationModel>
function selectRecords(state: RootState, table: 'providers'): Array<ProviderModel>
function selectRecords(state: RootState, table: 'todos'): Array<RealizedTodoModel>
function selectRecords(state: RootState, table: 'blobs'): Array<BlobModel>
function selectRecords(state: RootState, table: 'conversations'): Array<ConversationModel>
function selectRecords(state: RootState, table: 'messages'): Array<MessageModel>
function selectRecords(state: RootState, table: 'phone_calls'): Array<PhoneCallModel>
function selectRecords(state: RootState, table: SyncableTable): Array<SyncableModel>

function selectRecords(state: RootState, table: SyncableTable) {
  switch(table) {
    case 'expenses':
      return state.expenses.expenses
    case 'incidents':
      return state.incidents.incidents
    case 'attachments':
      return state.attachments.attachments
    case 'submissions':
      return state.submissions.submissions
    case 'advances':
      return state.advances?.advances || []
    case 'advance_requests':
      return state.advances?.advanceRequests || []
    case 'notifications':
      return state.notifications?.notifications || []
    case 'providers':
      return state.providers?.providers || []
    case 'todos':
      return state.todos?.todos || []
    case 'blobs':
      return state.blobs?.blobs || []
    case 'conversations':
      return state.conversations?.conversations || []
    case 'messages':
      return state.conversations?.messages || []
    case 'phone_calls':
      return state.conversations?.phoneCalls || []
    default:
      raiseUnknownSyncableTable(table)
  }
}
